import { urlConfig } from 'config/url-config';

import { IntercomChannel, QrScan, QrScanPayload, QrScanSource, QrScanType, UserReferrer } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

export type LeadActivity<Scan extends QrScan> =
    | { at: string; scan: Scan; chat?: undefined }
    | { at: string; chat: IntercomChannel; scan?: undefined };

export type QrScanLead<Scan extends QrScan> = {
    key: string;
    userId?: string;
    userInfo?: QrScan['userInfo'];
    scans: Scan[];
    chats: IntercomChannel[];
    activities: LeadActivity<Scan>[];
};

const QrScanHelpers = {
    urls: {
        scan: (type: QrScanType, id: string) => `/scan/${type}/${id}`,
        // Every scan is a visit at an RE+ event, and the RE+ referrer keeps the visitor in the Showtime experience
        withScanGate: (url: string, params: Record<string, string> = {}) =>
            `${url}?${new URLSearchParams({ [QrScanHelpers.gateParam]: '1', referrer: UserReferrer.REPLUS, ...params })}`,

        profileScanLanding: (
            company: { id: string; slug?: string | null },
            { userType = 'designer', params = {} }: { userType?: string; params?: Record<string, string> } = {},
        ) =>
            QrScanHelpers.urls.withScanGate(CompanyHelpers.urls.view(company.slug ?? company.id), {
                type: userType,
                ...params,
            }),
    },

    gateParam: 'qr',

    // Exhibitors scan their own codes to try them; those scans must not become leads
    testParam: 'test',

    isQrScan: (scan: Pick<QrScan, 'source'>) => scan.source !== QrScanSource.WEB,

    // Scans created before the type field existed have no type and were all profile scans
    isProfileScan: (scan: Pick<QrScan, 'type'>) => !scan.type || scan.type === QrScanType.PROFILE,

    // The visitor's most recent save of a profile (by company id), brochure or product, so every entry point opens the
    // same notes
    findLatestScan: (scans: QrScanPayload[], type: QrScanType, itemId: string) => {
        const isThisItem = (scan: QrScanPayload) =>
            ({
                [QrScanType.PROFILE]: QrScanHelpers.isProfileScan(scan) && scan.company === itemId,
                [QrScanType.COLLATERAL]: scan.collateral === itemId,
                [QrScanType.PRODUCT]: scan.product === itemId,
            })[type];

        return scans.filter(isThisItem).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
    },

    isLocked: (lead: Pick<QrScanLead<QrScan>, 'scans' | 'chats'>) =>
        [...lead.scans, ...lead.chats].every((item) => item.lockedPreview),

    visitorKey: (scan: QrScan) =>
        scan.user ?? scan.userInfo?.email?.toLowerCase() ?? scan.lockedPreview?.visitor ?? scan.id ?? '',

    groupByVisitor: <Scan extends QrScan>(scans: Scan[], chats: IntercomChannel[] = []): QrScanLead<Scan>[] => {
        const activitiesByVisitor = new Map<string, LeadActivity<Scan>[]>();

        const add = (key: string, activity: LeadActivity<Scan>) => {
            activitiesByVisitor.set(key, [...(activitiesByVisitor.get(key) ?? []), activity]);
        };

        for (const scan of scans) {
            add(QrScanHelpers.visitorKey(scan), { at: String(scan.createdAt ?? ''), scan });
        }

        for (const chat of chats) {
            add(chat.createdBy || chat.lockedPreview?.visitor || chat.id, {
                at: new Date(chat.latestMessage?.createdAt ?? chat.createdAt).toISOString(),
                chat,
            });
        }

        return [...activitiesByVisitor]
            .map(([key, visitorActivities]) => {
                const activities = [...visitorActivities].sort((a, b) => b.at.localeCompare(a.at));
                const visitorScans = activities.flatMap(({ scan }) => (scan ? [scan] : []));
                const visitorChats = activities.flatMap(({ chat }) => (chat ? [chat] : []));

                return {
                    key,
                    userId: visitorScans.find((scan) => scan.user)?.user ?? visitorChats[0]?.createdBy,
                    userInfo: visitorScans.find((scan) => scan.userInfo?.email)?.userInfo,
                    scans: visitorScans,
                    chats: visitorChats,
                    activities,
                };
            })
            .sort((a, b) => b.activities[0].at.localeCompare(a.activities[0].at));
    },

    generateDataURL: async (path: string) => {
        const { toDataURL } = await import('qrcode');

        return toDataURL(`${urlConfig.frontend}${path}`, { width: 2048, margin: 4 });
    },
};

export { QrScanHelpers };
